// utils/terminology.js
//
// Per-tenant display terminology.
//
// Only what the user READS changes. Code identifiers deliberately keep their
// original names — the Vendor model, /api/vendors, vendorAPI.js, component
// names, props, state and variables all stay "Vendor". Renaming those buys the
// client nothing and risks breaking the API contract.
//
// ── Why 'Supplier' is safe for mrwd, and only for mrwd ──────────────────────
// An accounting Vendor (backend/models/Vendor.js — tenant + accountingClient
// scoped, has TIN, drives AP / Form 2307 withholding / disbursements) is NOT
// the POS Supplier (backend/models/Supplier.js — tenant-scoped, paymentTerms /
// isFavorite / lastOrderDate, drives PurchaseOrder / GoodsReceipt). They are
// different collections holding different records.
//
// mrwd can call a Vendor a "Supplier" without ambiguity because mrwd never sees
// the POS Supplier: components/Sidebar.jsx excludes the 'purchasing' section
// (which owns "Suppliers" -> /suppliers) for tenantType 'mrwd'.
//
// DO NOT add a tenantType here that can reach the purchasing section — it would
// show that tenant two different menu items both labelled "Supplier", pointing
// at two different record sets.

import { useContext } from 'react';
import { AuthContext } from '../context/AuthContext';

const DEFAULT_VENDOR_TERMS = {
  one: 'Vendor',
  many: 'Vendors',
  lower: 'vendor',
  lowerMany: 'vendors',
};

const VENDOR_TERMS_BY_TENANT_TYPE = {
  mrwd: {
    one: 'Supplier',
    many: 'Suppliers',
    lower: 'supplier',
    lowerMany: 'suppliers',
  },
};

/**
 * Vendor terminology for a tenantType. Pure — use this where no hook is
 * available (non-component code, or a component that already holds the tenant).
 *
 * @param {string|null|undefined} tenantType
 * @returns {{one: string, many: string, lower: string, lowerMany: string}}
 *   Falls back to "Vendor" for any unknown or missing tenantType, so a tenant
 *   whose type has not loaded yet reads as the default rather than blank.
 */
export function getVendorTerms(tenantType) {
  return VENDOR_TERMS_BY_TENANT_TYPE[tenantType] || DEFAULT_VENDOR_TERMS;
}

/**
 * Hook form for components. Reads tenantType off the decoded access token,
 * which AuthContext spreads onto `user` — the JWT carries tenantType on login,
 * on refresh, and across switch-client (backend/utils/sessionConfig.js:36,
 * backend/routes/authRefresh.js:167/246/406), so this is synchronous and needs
 * no extra request.
 *
 * Deliberately reads the context directly rather than via useAuth(): useAuth
 * THROWS when there is no AuthProvider above it, and terminology is a display
 * concern that must never be the reason a component fails to render. Outside a
 * provider (unit tests rendering a component standalone, Storybook, an error
 * boundary fallback) this quietly yields the default "Vendor" terms.
 */
export function useVendorTerms() {
  const auth = useContext(AuthContext);
  return getVendorTerms(auth?.user?.tenantType);
}
